import { useEffect, useState, type CSSProperties, type JSX } from 'react';
import ImageItem, { type CategoryPlacement } from './ImageItem';
import { formatDimensions } from '../lib/formatDimensions';
import type { Painting } from '../types/painting';

interface MasonryGridProps {
  // Each inner array is one theme group (see Gallery's stylePriority
  // grouping). Column balancing (see assignColumnsForGroup) only swaps
  // cards within their own group, so a theme's cards stay together, but
  // column-height tracking runs continuously across groups rather than
  // resetting at each one — resetting would force every column to wait
  // for the previous group's tallest column to finish, which reads as a
  // large forced gap whenever that group didn't balance evenly.
  groupedPaintings: Painting[][];
  onSelect: (painting: Painting) => void;
}

// CSS Grid with a computed row-span per card, instead of the browser's plain
// multi-column layout. Multi-column fills top-to-bottom within one column
// before moving to the next, so scanning left-to-right across a row doesn't
// show sequential paintings, and column heights can end up quite uneven
// (a greedy fill, not a balanced one).
//
// Placement is computed explicitly (an accumulated height per column, each
// card assigned to whichever column is currently shortest) rather than left
// to the browser's grid auto-placement, so that each theme group's bottom
// edge comes out close to even instead of ragged — auto-placement fills
// strictly in source order and can't rebalance for that.
//
// Every number a card's span depends on is known before the card ever
// renders (imageWidth/imageHeight are captured at upload time, not measured
// from the loaded <img>), so the span is correct on the very first paint —
// nothing here is corrected after the fact. An earlier version measured
// each card's actual rendered height with a ResizeObserver and corrected
// the span once that arrived, which worked but meant every card's layout
// visibly jumped shortly after paint, tanking Cumulative Layout Shift.

// Mirrors Gallery's container (mx-auto max-w-6xl px-4) and this grid's own
// padding/gap classes below — used to work out the real column width for
// whatever breakpoint is currently active, since every card's row-span
// depends on it. A single fixed column width (this used to assume the
// desktop 4-column width for every breakpoint) badly under-reserves space
// on mobile's single-column layout, where the real column is far wider —
// every image then renders taller than its reserved space, overflowing
// into the next card.
const CONTAINER_MAX_WIDTH_PX = 1152; // Gallery's max-w-6xl
const CONTAINER_HORIZONTAL_PADDING_PX = 16; // Gallery's px-4, one side
const GRID_PADDING_PX = 16; // this grid's own p-4, one side
const COLUMN_GAP_PX = 16; // gap-x-4

const COLUMN_COUNT_BREAKPOINTS = [
  { minWidthPx: 1024, columns: 4 }, // lg
  { minWidthPx: 768, columns: 3 }, // md
  { minWidthPx: 640, columns: 2 }, // sm
  { minWidthPx: 0, columns: 1 },
];

function getColumnCount(viewportWidthPx: number): number {
  const breakpoint = COLUMN_COUNT_BREAKPOINTS.find((b) => viewportWidthPx >= b.minWidthPx);
  return breakpoint?.columns ?? 1;
}

function getColumnWidthPx(viewportWidthPx: number, columns: number): number {
  const containerContentWidthPx =
    Math.min(viewportWidthPx, CONTAINER_MAX_WIDTH_PX) - CONTAINER_HORIZONTAL_PADDING_PX * 2 - GRID_PADDING_PX * 2;
  const totalGapPx = (columns - 1) * COLUMN_GAP_PX;
  return (containerContentWidthPx - totalGapPx) / columns;
}

// Physically large paintings span 2 columns instead of 1 — Suhasini's own
// call on how the gallery should read, over an earlier continuous-scaling
// experiment: a plain 4-column grid, with the larger paintings given two
// columns. The threshold sits in a natural gap in the real data: the
// biggest 9 paintings (Girl with Lamb 60x42in down to The Fighting
// Temeraire 35x48in) all clear 1650 sq in; the next-biggest tops out at
// 1216 sq in.
const LARGE_AREA_SQ_IN = 1650;

function paintingAreaSqIn(painting: Painting): number {
  return painting.height * painting.width;
}

function desiredColumnSpan(painting: Painting): number {
  return paintingAreaSqIn(painting) >= LARGE_AREA_SQ_IN ? 2 : 1;
}

interface GridMetrics {
  columnWidthPx: number;
  columnCount: number;
}

function useGridMetrics(): GridMetrics {
  const [viewportWidthPx, setViewportWidthPx] = useState(() => document.documentElement.clientWidth);

  useEffect(() => {
    function handleResize(): void {
      setViewportWidthPx(document.documentElement.clientWidth);
    }
    window.addEventListener('resize', handleResize);
    return (): void => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const columnCount = getColumnCount(viewportWidthPx);
  return { columnWidthPx: getColumnWidthPx(viewportWidthPx, columnCount), columnCount };
}

// A single flat text-block height estimate either overflows into the next
// card (title too long/wraps more than expected) or, for the common case of
// a short one-line title, wastes a lot of vertical space. Measuring text
// width with a canvas — synchronously, before paint, same as everything
// else this component computes — lets each card's estimate reflect its own
// actual title length instead of assuming a worst case for every card.
let measureContext: CanvasRenderingContext2D | null | undefined;

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureContext === undefined) {
    measureContext = document.createElement('canvas').getContext('2d');
  }
  return measureContext;
}

// A hardcoded font stack for canvas measurement can silently mismatch
// what's actually rendered — canvas's font resolution doesn't necessarily
// support the same generic keywords as CSS, and did in practice measure a
// visibly wider font than Tailwind's classes render with here, causing
// titles to be predicted to wrap when they didn't. Reading the *computed*
// font off a real (hidden) element carrying the same classes as the actual
// title/style text guarantees the two stay in sync regardless of platform.
const fontCache = new Map<string, string>();

function getComputedFont(className: string): string {
  const cached = fontCache.get(className);
  if (cached !== undefined) return cached;

  const probe = document.createElement('span');
  probe.className = className;
  probe.style.position = 'fixed';
  probe.style.visibility = 'hidden';
  probe.style.left = '-9999px';
  document.body.appendChild(probe);
  const font = getComputedStyle(probe).font;
  document.body.removeChild(probe);

  fontCache.set(className, font);
  return font;
}

// A conservative few pixels below the real available width: real visitors'
// devices don't all render text with identical metrics, and a card
// overflowing into the next one (underestimating) is a much worse outcome
// than a few extra pixels of whitespace (overestimating), so this errs
// toward assuming a line wraps a little sooner than strictly measured.
const WRAP_SAFETY_MARGIN_PX = 2;

function measureTextWidth(text: string, font: string): number {
  const ctx = getMeasureContext();
  if (ctx === null) return 0;
  ctx.font = font;
  return ctx.measureText(text).width;
}

function estimateWrappedLineCount(text: string, font: string, maxWidthPx: number): number {
  if (text.length === 0) return 1;
  const availableWidthPx = maxWidthPx - WRAP_SAFETY_MARGIN_PX;
  const spaceWidth = measureTextWidth(' ', font);

  const words = text.split(' ');
  let lineCount = 1;
  let currentLineWidth = 0;
  for (const word of words) {
    const wordWidth = measureTextWidth(word, font);
    const widthIfAdded = currentLineWidth === 0 ? wordWidth : currentLineWidth + spaceWidth + wordWidth;
    if (widthIfAdded > availableWidthPx && currentLineWidth > 0) {
      lineCount++;
      currentLineWidth = wordWidth;
    } else {
      currentLineWidth = widthIfAdded;
    }
  }
  return lineCount;
}

// If there's room, the single category tag rides on the title row (first
// choice) or the style row (second choice) instead of taking its own row
// below — saving that row's height for the common case of short text,
// while a title/style combination that wouldn't leave adequate room next
// to the tag just keeps the tag on its own row, unchanged. "Adequate room"
// means a real minimum gap, not just barely not-overlapping — a tag jammed
// right up against the title/style text would look cramped.
const CATEGORY_PILL_HORIZONTAL_PADDING_PX = 20; // px-2.5, both sides
const CATEGORY_PILL_BORDER_PX = 2; // border, both sides
const MIN_GAP_PX = 12;

function categoryPillWidth(painting: Painting): number {
  return (
    measureTextWidth(painting.category[0] ?? '', getComputedFont('text-xs')) +
    CATEGORY_PILL_HORIZONTAL_PADDING_PX +
    CATEGORY_PILL_BORDER_PX
  );
}

const TITLE_LINE_HEIGHT_PX = 32; // Tailwind text-2xl's default line-height
const STYLE_LINE_HEIGHT_PX = 20; // Tailwind text-sm's default line-height
const STYLE_ROW_DIMENSION_RESERVE_PX = 60; // roughly what "19x26" takes on the same row
// mt-2 above the text block and mt-2 between the style row and category
// pills, when the pill needs its own row (i.e. neither fallback fits).
const TEXT_BLOCK_MARGINS_PX = 8 + 8;
const CATEGORY_ROW_PX = 24;

interface TextBlockLayout {
  heightPx: number;
  categoryPlacement: CategoryPlacement;
}

function estimateTextBlockLayout(painting: Painting, columnWidthPx: number): TextBlockLayout {
  const availableWidthPx = columnWidthPx - WRAP_SAFETY_MARGIN_PX;
  const titleFont = getComputedFont('text-2xl font-bold');
  const styleFont = getComputedFont('text-sm');
  const styleText = `Style: ${painting.style.join(', ')}`;
  const dimensionText = formatDimensions(painting.height, painting.width);

  const titleLines = estimateWrappedLineCount(painting.title, titleFont, columnWidthPx);
  const hasSingleCategory = painting.category.length === 1;
  const pillWidth = hasSingleCategory ? categoryPillWidth(painting) : 0;

  if (hasSingleCategory && titleLines === 1) {
    const titleWidth = measureTextWidth(painting.title, titleFont);
    if (titleWidth + MIN_GAP_PX + pillWidth <= availableWidthPx) {
      const styleLines = estimateWrappedLineCount(styleText, styleFont, columnWidthPx - STYLE_ROW_DIMENSION_RESERVE_PX);
      return {
        heightPx: TITLE_LINE_HEIGHT_PX + styleLines * STYLE_LINE_HEIGHT_PX + TEXT_BLOCK_MARGINS_PX,
        categoryPlacement: 'title',
      };
    }
  }

  if (hasSingleCategory) {
    const dimensionWidth = measureTextWidth(dimensionText, styleFont);
    const styleAvailableWidthPx = availableWidthPx - dimensionWidth - pillWidth - 2 * MIN_GAP_PX;
    const styleLinesWithPill = estimateWrappedLineCount(styleText, styleFont, styleAvailableWidthPx);
    if (styleLinesWithPill === 1) {
      return {
        heightPx: titleLines * TITLE_LINE_HEIGHT_PX + STYLE_LINE_HEIGHT_PX + TEXT_BLOCK_MARGINS_PX,
        categoryPlacement: 'styleRow',
      };
    }
  }

  const styleLines = estimateWrappedLineCount(styleText, styleFont, columnWidthPx - STYLE_ROW_DIMENSION_RESERVE_PX);
  const categoryRowPx = painting.category.length > 0 ? CATEGORY_ROW_PX : 0;
  return {
    heightPx: titleLines * TITLE_LINE_HEIGHT_PX + styleLines * STYLE_LINE_HEIGHT_PX + categoryRowPx + TEXT_BLOCK_MARGINS_PX,
    categoryPlacement: 'stacked',
  };
}

// A small row unit keeps the rounding-up slack in each card's reserved space
// (at most one row unit) too small to visibly throw off the gap below it.
const ROW_UNIT_PX = 4;
// Vertical spacing between stacked cards in the same column. A grid item's
// own margin can't do this: margin only affects the item's box within its
// own grid area, never where the *next* grid item is placed — that's decided
// purely by row-track lines (i.e. by span). So the gap has to be folded into
// the span calculation itself, reserving extra track space the card doesn't
// use, rather than applied as margin on the card.
const CARD_GAP_PX = 16;
// Roughly two rows' worth on the widest (4-column) breakpoint — enough to
// cover what's actually above the fold on most screens, without eagerly
// fetching and competing for bandwidth with the rest of the gallery.
const PRIORITY_CARD_COUNT = 8;

function paintingAspectRatio(painting: Painting): number {
  if (painting.imageWidth !== undefined && painting.imageHeight !== undefined) {
    return painting.imageHeight / painting.imageWidth;
  }
  // Older paintings uploaded before imageWidth/imageHeight were captured.
  // The physical painting's size is a poor stand-in for the photo's own
  // framing, but it's the only thing available until re-saved.
  return painting.height / painting.width;
}

function calculateRowSpan(imageHeightPx: number, textBlockHeightPx: number): number {
  return Math.ceil((imageHeightPx + textBlockHeightPx + CARD_GAP_PX) / ROW_UNIT_PX);
}

// A card's actual pixel width once its column span and the gap it now
// spans across are accounted for.
function cardWidthForSpan(columnSpan: number, columnWidthPx: number): number {
  return columnSpan * columnWidthPx + (columnSpan - 1) * COLUMN_GAP_PX;
}

const gridStyle: CSSProperties = {
  gridAutoRows: `${ROW_UNIT_PX}px`,
  // Left at 0 deliberately: CSS grid inserts a full row-gap between every
  // implicit ROW_UNIT_PX-tall track a card's span crosses, so with a
  // non-zero row-gap the reserved grid area for a tall span becomes far
  // bigger than the card's actual (align-self: start) content height,
  // leaving a big blank band before the next card in that column.
  // CARD_GAP_PX above is the real source of vertical spacing instead.
  rowGap: 0,
};

interface CardLayout {
  painting: Painting;
  columnStart: number; // 1-indexed grid line
  columnSpan: number;
  rowStart: number; // 1-indexed grid line, in ROW_UNIT_PX units
  rowSpan: number;
  categoryPlacement: CategoryPlacement;
  priority: boolean;
}

// Sum of squared deviations from the mean column height — small when
// columns are close to even, large when one or two columns dominate.
// Squaring (rather than e.g. max-min range) means a swap that meaningfully
// helps the worst column wins out over one that only shuffles two
// already-close columns, and it stays well-behaved near ties instead of
// flip-flopping.
function columnImbalance(columnHeights: number[]): number {
  const mean = columnHeights.reduce((sum, h) => sum + h, 0) / columnHeights.length;
  return columnHeights.reduce((sum, h) => sum + (h - mean) ** 2, 0);
}

// Ignore swaps that only help by a sliver — otherwise the search can churn
// through swaps with no visible effect on the rendered layout.
const IMBALANCE_IMPROVEMENT_EPSILON = 1;
const MAX_SWAP_PASSES = 6;

// Assigns each card in a group to a column in two stages. First, a greedy
// pass in the cards' original (creation) order, each card going to
// whichever column is shortest so far — ties favor the left-most column.
// Sorting cards tallest-first first would balance columns better on its
// own, but visibly clusters every tall painting at the top and every short
// one at the bottom, which reads as monotonous rather than a deliberate
// arrangement. Keeping the original order preserves that visual variety.
//
// Second, a local-search refinement: repeatedly try swapping which column
// each pair of cards landed in, keeping a swap only when it measurably
// reduces columnImbalance. This nudges the greedy result toward better
// balance — the source of Radha Krishna and Peacock's uneven placement —
// without fully resorting the group by height.
//
// startingHeights is each column's height *before* this group — carried
// over from whatever the previous group left behind rather than reset to
// zero, so a theme's first card lands wherever a column is actually
// shortest instead of every column waiting for the group before it to
// finish. A short column can still be evened out by a later theme's cards
// this way, rather than sitting under a forced gap until it does.
function assignColumnsForGroup(rowSpans: number[], columnCount: number, startingHeights: number[]): number[] {
  const columnAssignment = new Array<number>(rowSpans.length).fill(0);
  const columnHeights = startingHeights.slice();

  rowSpans.forEach((rowSpan, index) => {
    let targetColumn = 0;
    for (let column = 1; column < columnCount; column++) {
      if ((columnHeights[column] ?? 0) < (columnHeights[targetColumn] ?? 0)) targetColumn = column;
    }
    columnAssignment[index] = targetColumn;
    columnHeights[targetColumn] = (columnHeights[targetColumn] ?? 0) + rowSpan;
  });

  for (let pass = 0; pass < MAX_SWAP_PASSES; pass++) {
    let improvedThisPass = false;

    for (let i = 0; i < rowSpans.length; i++) {
      for (let j = i + 1; j < rowSpans.length; j++) {
        const columnA = columnAssignment[i] ?? 0;
        const columnB = columnAssignment[j] ?? 0;
        if (columnA === columnB) continue;

        const spanI = rowSpans[i] ?? 0;
        const spanJ = rowSpans[j] ?? 0;
        const heightA = columnHeights[columnA] ?? 0;
        const heightB = columnHeights[columnB] ?? 0;

        const before = columnImbalance(columnHeights);
        const trialHeights = columnHeights.slice();
        trialHeights[columnA] = heightA - spanI + spanJ;
        trialHeights[columnB] = heightB - spanJ + spanI;
        const after = columnImbalance(trialHeights);

        if (after + IMBALANCE_IMPROVEMENT_EPSILON < before) {
          columnAssignment[i] = columnB;
          columnAssignment[j] = columnA;
          columnHeights[columnA] = trialHeights[columnA] ?? 0;
          columnHeights[columnB] = trialHeights[columnB] ?? 0;
          improvedThisPass = true;
        }
      }
    }

    if (!improvedThisPass) break;
  }

  return columnAssignment;
}

interface WideCardPlacement {
  painting: Painting;
  columnStart: number; // 0-indexed
  columnSpan: number;
  rowStartOffset: number; // height (in ROW_UNIT_PX units) already used in the placed block, before this card
  rowSpan: number;
  categoryPlacement: CategoryPlacement;
}

// Places wide (columnSpan > 1) cards, in original order, into whichever
// contiguous block of columns is currently shortest — the same greedy idea
// as assignColumnsForGroup's first pass, generalized from one column to N
// adjacent ones. No swap-refinement for these: physically large paintings
// are a small minority of any group, so the handful of possible orderings
// don't leave much for a local search to improve on, and mixing wide and
// normal cards into one swap search would need swaps to trade unequal
// amounts of column width, which stops being a simple exchange.
function assignWideCardColumns(
  paintings: Painting[],
  columnWidthPx: number,
  columnCount: number,
  columnHeights: number[],
): WideCardPlacement[] {
  const placements: WideCardPlacement[] = [];

  for (const painting of paintings) {
    const columnSpan = Math.min(desiredColumnSpan(painting), columnCount);
    const cardWidthPx = cardWidthForSpan(columnSpan, columnWidthPx);
    const imageHeightPx = cardWidthPx * paintingAspectRatio(painting);
    const { heightPx: textBlockHeightPx, categoryPlacement } = estimateTextBlockLayout(painting, cardWidthPx);
    const rowSpan = calculateRowSpan(imageHeightPx, textBlockHeightPx);

    let bestColumnStart = 0;
    let bestBlockHeight = Number.POSITIVE_INFINITY;
    for (let start = 0; start <= columnCount - columnSpan; start++) {
      let blockHeight = 0;
      for (let column = start; column < start + columnSpan; column++) {
        blockHeight = Math.max(blockHeight, columnHeights[column] ?? 0);
      }
      if (blockHeight < bestBlockHeight) {
        bestBlockHeight = blockHeight;
        bestColumnStart = start;
      }
    }

    for (let column = bestColumnStart; column < bestColumnStart + columnSpan; column++) {
      columnHeights[column] = bestBlockHeight + rowSpan;
    }

    placements.push({
      painting,
      columnStart: bestColumnStart,
      columnSpan,
      rowStartOffset: bestBlockHeight,
      rowSpan,
      categoryPlacement,
    });
  }

  return placements;
}

function computeCardLayouts(groupedPaintings: Painting[][], columnWidthPx: number, columnCount: number): CardLayout[] {
  const layouts: CardLayout[] = [];
  let cardIndex = 0;
  // Tracks each column's height across the *whole* gallery, not reset per
  // group — see the comment on assignColumnsForGroup for why.
  const columnHeights = new Array<number>(columnCount).fill(0);

  for (const group of groupedPaintings) {
    const widePaintings = group.filter((painting) => desiredColumnSpan(painting) > 1 && columnCount > 1);
    const normalPaintings = group.filter((painting) => !widePaintings.includes(painting));

    // Wide cards are placed first, in their original order, so the normal
    // cards' balance pass below starts from columns that already reflect
    // this group's wide cards.
    for (const {
      painting,
      columnStart,
      columnSpan,
      rowStartOffset,
      rowSpan,
      categoryPlacement,
    } of assignWideCardColumns(widePaintings, columnWidthPx, columnCount, columnHeights)) {
      layouts.push({
        painting,
        columnStart: columnStart + 1,
        columnSpan,
        rowStart: 1 + rowStartOffset,
        rowSpan,
        categoryPlacement,
        priority: cardIndex < PRIORITY_CARD_COUNT,
      });
      cardIndex++;
    }

    const cardsWithSpans = normalPaintings.map((painting) => {
      const imageHeightPx = columnWidthPx * paintingAspectRatio(painting);
      const { heightPx: textBlockHeightPx, categoryPlacement } = estimateTextBlockLayout(painting, columnWidthPx);
      return { painting, rowSpan: calculateRowSpan(imageHeightPx, textBlockHeightPx), categoryPlacement };
    });

    const columnAssignment = assignColumnsForGroup(
      cardsWithSpans.map((card) => card.rowSpan),
      columnCount,
      columnHeights,
    );

    // Re-derive each column's stacking order from the final assignment,
    // walking cards in their original order so a column's own contents
    // still read top-to-bottom in a natural sequence — continuing from
    // (and updating) the running columnHeights, so the next group picks up
    // exactly where this one left off.
    cardsWithSpans.forEach((card, index) => {
      const column = columnAssignment[index] ?? 0;
      layouts.push({
        painting: card.painting,
        columnStart: column + 1,
        columnSpan: 1,
        rowStart: 1 + (columnHeights[column] ?? 0),
        rowSpan: card.rowSpan,
        categoryPlacement: card.categoryPlacement,
        priority: cardIndex < PRIORITY_CARD_COUNT,
      });
      columnHeights[column] = (columnHeights[column] ?? 0) + card.rowSpan;
      cardIndex++;
    });
  }

  return layouts;
}

export default function MasonryGrid({ groupedPaintings, onSelect }: MasonryGridProps): JSX.Element {
  const { columnWidthPx, columnCount } = useGridMetrics();
  const cardLayouts = computeCardLayouts(groupedPaintings, columnWidthPx, columnCount);

  return (
    <div className="grid grid-cols-1 gap-x-4 p-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" style={gridStyle}>
      {cardLayouts.map(({ painting, columnStart, columnSpan, rowStart, rowSpan, categoryPlacement, priority }) => {
        const itemStyle: CSSProperties = {
          gridColumnStart: columnStart,
          gridColumnEnd: `span ${columnSpan}`,
          gridRowStart: rowStart,
          gridRowEnd: `span ${rowSpan}`,
          alignSelf: 'start',
        };
        return (
          <div key={painting.id} style={itemStyle}>
            <ImageItem
              painting={painting}
              onSelect={onSelect}
              priority={priority}
              categoryPlacement={categoryPlacement}
            />
          </div>
        );
      })}
    </div>
  );
}
